import React, { useState, useContext } from 'react'
import { FormattedMessage, FormattedDate } from 'react-intl'
import Icon from 'components/Icon'
import OrderStage from 'components/OrderStage'
import OrderActionsButton from './../OrderActionsButton'
import messages from './messages'
import { Chip } from '@arnsbogroup/react-voicearchive'
import { BrandContext } from 'CustomerApp'
import { getUsageChainTails } from 'utils/usageChainHelpers'
import UsageStatusChip from 'components/UsageStatusChip'

const Components = {
  Text: ({ value }) => (
    <td className="px-1 xl:px-4 py-2 xl:py-3 whitespace-nowrap text-sm max-w-0">
      <div className="truncate">{ value }</div>
    </td>
  ),
  SpotDuration: ({ value }) => (
    <td className="px-1 xl:px-4 py-2 xl:py-3 whitespace-nowrap text-sm max-w-0">
      <div className="truncate">{ value }</div>
    </td>
  ),
}

const OrderRow = ({ compact = false, order, columns, onClick, isSelected }) => {
  const [showDetailsPane, setShowDetailsPane] = useState(false)
  const { brand } = useContext(BrandContext)

  let { stage, uid, crdate, deadline, customer_name, name, customerReference, contact_name, contact_email, contact_image, orderusage_name, medias, order_template_uid, tasks } = order

  if (!contact_email) {
    contact_name = 'Not assigned'
    contact_email = '-'
  }

  // Find USAGE chain tails
  const usageTasks = getUsageChainTails(tasks || [])

  // Determine the most urgent usage task to display (earliest expiry or pending start)
  let mostUrgentUsageTask = null

  if (usageTasks.length > 0) {
    const taskStatuses = usageTasks.map(task => {
      const usageData = task.usage || {}
      const usageTaskStatus = usageData.usageTaskStatus
      const usageTaskDaysUntilStart = usageData.usageTaskDaysUntilStart
      const usageRemainingDays = usageData.usageRemainingDays
      const usageSlug = usageData.slug

      const isInPerpetuity = usageSlug == 'in_perpetuity'

      if (usageTaskStatus === 'pending' && usageTaskDaysUntilStart) {
        return { status: 'pending', days: usageTaskDaysUntilStart, task }
      } else if (isInPerpetuity && usageTaskStatus === 'active') {
        // In Perpetuity tasks - show but with lowest priority (infinite days)
        return { status: 'perpetuity', days: Infinity, task }
      } else if (usageRemainingDays !== undefined && usageRemainingDays !== null) {
        if (usageRemainingDays > 0) {
          return { status: 'active', days: usageRemainingDays, task }
        } else {
          return { status: 'expired', days: 0, task }
        }
      }
      return null
    }).filter(Boolean)

    if (taskStatuses.length > 0) {
      // Priority: Show expired first, then active (earliest), then pending (earliest), then perpetuity (lowest priority)
      const expiredTasks = taskStatuses.filter(t => t.status === 'expired')
      const activeTasks = taskStatuses.filter(t => t.status === 'active')
      const pendingTasks = taskStatuses.filter(t => t.status === 'pending')
      const perpetuityTasks = taskStatuses.filter(t => t.status === 'perpetuity')

      if (expiredTasks.length > 0) {
        mostUrgentUsageTask = expiredTasks[0].task
      } else if (activeTasks.length > 0) {
        // Show earliest active expiry
        const earliest = activeTasks.reduce((min, t) => t.days < min.days ? t : min)
        mostUrgentUsageTask = earliest.task
      } else if (pendingTasks.length > 0) {
        // Show earliest pending start
        const earliest = pendingTasks.reduce((min, t) => t.days < min.days ? t : min)
        mostUrgentUsageTask = earliest.task
      } else if (perpetuityTasks.length > 0) {
        // Show first In Perpetuity task if no other urgent tasks
        mostUrgentUsageTask = perpetuityTasks[0].task
      }
    }
  }

  return (
    <tr
      key={uid}
      className={`py-2 ${stage == 'AMENDING' ? 'bg-yellow-50 hover:bg-yellow-100' : 'hover:bg-gray-100'} cursor-pointer border-b border-gray-100 ${isSelected ? 'shadow-inner bg-blue-100' : ''}`}
      onClick={onClick}
      tabIndex={0}
    >
      <td className="px-1 xl:px-4 py-2 xl:py-3 whitespace-nowrap text-sm max-w-0">
        <div className="truncate">#{ uid }</div>
      </td>

      {!compact && (
        <td className="px-1 xl:px-4 py-2 xl:py-3 whitespace-nowrap text-sm max-w-0">
          <div className="truncate" title={ customerReference }>
            {customerReference ? (
              <span className="text-gray-900">{ customerReference }</span>
            ) : (
              <span className="text-gray-200">
                <FormattedMessage {...messages.notSupplied} />
              </span>
            )}
          </div>
        </td>
      )}

      {!compact && (
        <td className="px-1 xl:px-4 py-2 xl:py-3 whitespace-nowrap text-sm max-w-0">
          <div className="truncate">
            <FormattedDate value={new Date(crdate * 1000)} />
          </div>
        </td>
      )}

      {!compact && (
        <td className="px-1 xl:px-4 py-2 xl:py-3 whitespace-nowrap text-sm max-w-0">
          <div className="truncate">
            <FormattedDate value={new Date(deadline * 1000)} />
          </div>
        </td>
      )}

      <td className="px-1 xl:px-4 py-2 xl:py-3 whitespace-nowrap text-sm max-w-0">
        <div className="truncate">{ name }</div>
      </td>

      <td className="px-1 xl:px-4 py-2 xl:py-3 whitespace-nowrap text-sm text-gray-400">
        <OrderStage stage={stage} />
      </td>

      {!compact && (
        <td className="px-1 xl:px-4 py-2 xl:py-3 whitespace-nowrap text-sm max-w-0">
          <div className="truncate">{ customer_name }</div>
        </td>
      )}

      {!compact && (
        <td
          className="px-1 xl:px-4 py-2 xl:py-3 whitespace-nowrap"
          title={`${contact_name} (${contact_email})`}
        >
          <div className="flex items-center w-full min-w-0 justify-center xl:justify-start">
            <div className="shrink-0 h-8 w-8">
              {contact_image ? (
                <img className="h-8 w-8 rounded-full" src={contact_image} alt="" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-gray-200 flex justify-center items-center">
                  <Icon icon="user" />
                </div>
              )}
            </div>

            <div className="ml-4 hidden xl:block min-w-0">
              <div className="text-sm font-medium text-gray-900">
                { contact_name }
              </div>
              <div className="text-sm text-gray-500 truncate">
                { contact_email }
              </div>
            </div>
          </div>
        </td>
      )}

      {!compact && (
        <td className="px-1 xl:px-4 py-2 xl:py-3 whitespace-nowrap text-sm space-y-1">
          <div className="flex gap-1 flex-wrap min-w-0">
            {!!orderusage_name && !mostUrgentUsageTask && (
              <Chip>{orderusage_name}</Chip>
            )}

            {medias && !mostUrgentUsageTask && !!medias.length &&
              medias.map(({ name }) => (
                <Chip key={name}>{name}</Chip>
              ))
            }

            {mostUrgentUsageTask && (
              <UsageStatusChip task={mostUrgentUsageTask} />
            )}
          </div>
        </td>
      )}

      { !compact && columns.map(({ key, component }) => (
        React.createElement(Components[component], {
          key: `${key}-${uid}`,
          value: order[key]
        })
      ))}

      <td className="w-px px-1 xl:px-2 py-2 xl:py-3">
        <div className="w-12 h-full flex items-center justify-center">
          { (brand.voiceflow && order_template_uid) ? (
            <OrderActionsButton id={uid} />
          ) : (
            <Icon icon="chevron-right" />
          )}
        </div>
      </td>
    </tr>
  )
}

export default OrderRow
